import { useMsal } from '@azure/msal-react';
import { makeStyles, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, tokens } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import { useStores } from '../../../common/hooks';
import { AiDisqualificationBucket, aiDisqualificationBuckets, AiScrubScope, HumanScrubBand, humanScrubBands, scoreAgreementBand } from '../../../models';
import { EvaluationComparisonUiStore } from '../evaluation-comparison-ui-store';

interface EvaluationComparisonPanelProps {
  awardId: number;
  scope?: AiScrubScope;
  countryId?: number;
}

const humanBandLabels: Record<HumanScrubBand, string> = {
  'human-disqualified': 'Human disqualified',
  'human-passed': 'Human passed',
  'no-human-label': 'No human label',
};

const aiBucketLabels: Record<AiDisqualificationBucket, string> = {
  'ai-rule-disqualified': 'AI disqualified (rule)',
  'ai-llm-disqualified': 'AI disqualified (LLM)',
  'ai-passed': 'AI passed',
  'ai-did-not-complete': "AI didn't finish",
};

const scaleNormalizationNote = 'The AI overall score and the human weighted-mean aggregate are both 0-5 weighted means and directly comparable; rank-order agreement (Spearman) is also reported.';

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  rowHeaderCell: {
    fontWeight: tokens.fontWeightSemibold,
  },
  totalCell: {
    fontWeight: tokens.fontWeightSemibold,
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
});

const EvaluationComparisonPanelImpl: React.FC<EvaluationComparisonPanelProps> = (props) => {
  const { awardId, scope = 'awards', countryId } = props;
  const { instance } = useMsal();
  const { apiStore } = useStores();
  const styles = useStyles();
  const store = useMemo(() => new EvaluationComparisonUiStore(), []);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      store.setIsLoading(true);
      try {
        const rows = await apiStore.getEvaluationComparison(instance, awardId, scope, countryId);
        if (!cancelled) store.setRows(rows ?? []);
      } catch {
        if (!cancelled) store.setRows([]);
      } finally {
        if (!cancelled) store.setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [awardId, scope, countryId, instance, apiStore, store]);

  const renderHeaderRow = (): React.JSX.Element => (
    <TableRow>
      <TableHeaderCell />
      {aiDisqualificationBuckets.map((bucket) => (
        <TableHeaderCell key={bucket}>{aiBucketLabels[bucket]}</TableHeaderCell>
      ))}
      <TableHeaderCell>Total</TableHeaderCell>
    </TableRow>
  );

  const renderBandRow = (band: HumanScrubBand): React.JSX.Element => (
    <TableRow key={band}>
      <TableCell className={styles.rowHeaderCell}>{humanBandLabels[band]}</TableCell>
      {aiDisqualificationBuckets.map((bucket) => (
        <TableCell key={bucket}>{store.matrix[band][bucket]}</TableCell>
      ))}
      <TableCell className={styles.totalCell}>{store.bandTotals[band]}</TableCell>
    </TableRow>
  );

  const renderTotalsRow = (): React.JSX.Element => (
    <TableRow>
      <TableCell className={styles.totalCell}>Total</TableCell>
      {aiDisqualificationBuckets.map((bucket) => (
        <TableCell key={bucket} className={styles.totalCell}>{store.columnTotals[bucket]}</TableCell>
      ))}
      <TableCell className={styles.totalCell}>{store.grandTotal}</TableCell>
    </TableRow>
  );

  const renderMatrix = (): React.JSX.Element => (
    <Table aria-label='AI versus human disqualification correspondence matrix'>
      <TableHeader>{renderHeaderRow()}</TableHeader>
      <TableBody>
        {humanScrubBands.map((band) => renderBandRow(band))}
        {renderTotalsRow()}
      </TableBody>
    </Table>
  );

  const renderCaption = (): React.JSX.Element => (
    <Text size={200} className={styles.caption}>
      Counts reflect only genuine human scrub labels; the &quot;No human label&quot; band holds AI rule-layer disqualifications and nominations no human reviewed. The &quot;AI disqualified (rule)&quot; column includes deterministic foreign-language detections, shown as observed behaviour and not graded for correctness. A nomination disqualified by the rule layer appears in that column and any coexisting LLM verdict is not shown, because the rule gate is deterministic and runs first.
    </Text>
  );

  const renderAiDisqualificationTable = (): React.JSX.Element => (
    <Table aria-label='AI LLM disqualification reasons'>
      <TableHeader>
        <TableRow>
          <TableHeaderCell>Nomination</TableHeaderCell>
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell>AI reason</TableHeaderCell>
          <TableHeaderCell>AI rationale</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {store.aiLlmDisqualifications.map((dq) => (
          <TableRow key={dq.nominationNumber}>
            <TableCell>{dq.nominationNumber}</TableCell>
            <TableCell>{dq.name}</TableCell>
            <TableCell>{dq.aiDisqualificationReason ?? '-'}</TableCell>
            <TableCell>{dq.aiDisqualificationRationale ?? '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderAiDisqualificationSection = (): React.JSX.Element => (
    <>
      <Text weight='semibold' size={400}>AI disqualification reasons (LLM)</Text>
      {store.aiLlmDisqualifications.length === 0
        ? <Text>No AI LLM disqualifications for this award.</Text>
        : renderAiDisqualificationTable()}
    </>
  );

  const renderScoreIndicator = (): React.JSX.Element => {
    const rho = store.scoreAgreement;
    if (rho === null) {
      return <Text>Not enough scored nominations for a rank-order agreement (need at least two with score variation).</Text>;
    }
    return (
      <Text weight='semibold'>
        Rank-order agreement (Spearman rho): {rho.toFixed(2)} ({scoreAgreementBand(rho)}) - n={store.sortedScorePairs.length} scored
      </Text>
    );
  };

  const renderScoreNote = (): React.JSX.Element => (
    <Text size={200} className={styles.caption}>
      {scaleNormalizationNote} Only nominations with both a human aggregate score and a completed AI overall score appear here - disqualified nominations are never judged and so are excluded.
    </Text>
  );

  const renderScoreTable = (): React.JSX.Element => (
    <Table aria-label='AI versus human overall score side by side'>
      <TableHeader>
        <TableRow>
          <TableHeaderCell>Nomination</TableHeaderCell>
          <TableHeaderCell>Human aggregate</TableHeaderCell>
          <TableHeaderCell>AI overall</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {store.sortedScorePairs.map((pair) => (
          <TableRow key={pair.nominationNumber}>
            <TableCell>{pair.nominationNumber}</TableCell>
            <TableCell>{pair.humanAggregateScore.toFixed(2)}</TableCell>
            <TableCell>{pair.aiOverallScore.toFixed(2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderScoreSection = (): React.JSX.Element => (
    <>
      <Text weight='semibold' size={400}>AI vs human overall score</Text>
      {store.sortedScorePairs.length === 0
        ? <Text>No scored nominations to compare for this award.</Text>
        : (<>{renderScoreIndicator()}{renderScoreTable()}</>)}
      {renderScoreNote()}
    </>
  );

  const renderWinnerScoredLowerTable = (): React.JSX.Element => (
    <Table aria-label='Winners the AI ranked lower than the human outcome'>
      <TableHeader>
        <TableRow>
          <TableHeaderCell>Nomination</TableHeaderCell>
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell>Human aggregate</TableHeaderCell>
          <TableHeaderCell>AI overall</TableHeaderCell>
          <TableHeaderCell>AI rationale</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {store.winnersScoredLower.map((winner) => (
          <TableRow key={winner.nominationNumber}>
            <TableCell>{winner.nominationNumber}</TableCell>
            <TableCell>{winner.name}</TableCell>
            <TableCell>{winner.humanAggregateScore.toFixed(2)}</TableCell>
            <TableCell>{winner.aiOverallScore.toFixed(2)}</TableCell>
            <TableCell>{winner.aiOverallRationale ?? '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderWinnerScoredLowerSection = (): React.JSX.Element => (
    <>
      <Text weight='semibold' size={400}>Winners the AI would have ranked lower</Text>
      {store.winnersScoredLower.length === 0
        ? <Text>No winner was ranked lower by the AI for this award.</Text>
        : renderWinnerScoredLowerTable()}
      <Text size={200} className={styles.caption}>
        {scaleNormalizationNote} A winner is listed only when its AI rank sits below its human rank across the scored cohort.
      </Text>
    </>
  );

  if (store.isLoading) {
    return <Spinner label='Loading comparison...' />;
  }

  return (
    <div className={styles.panel}>
      <Text weight='semibold' size={400}>AI vs human disqualification matrix</Text>
      {store.grandTotal === 0 ? <Text>No submitted nominations to compare for this award.</Text> : renderMatrix()}
      {renderCaption()}
      {renderAiDisqualificationSection()}
      {renderScoreSection()}
      {renderWinnerScoredLowerSection()}
    </div>
  );
};

export const EvaluationComparisonPanel = observer(EvaluationComparisonPanelImpl);
